import React from "react";
import { IonSpinner, useIonToast } from "@ionic/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import { IconRefresh, IconPhoneCall } from "@tabler/icons-react";
import classNames from "classnames";
import ReactGA from "react-ga4";

import SkeletonText from "../SkeletonText";
import ChatDetail from "../chat-detail";
import ChatInput from "../common/chat-input";
import FileInput from "../common/file-input";
import UploadPhotoModal from "../UploadPhotoModal";
import Call from "../common/call";

import { Message, Settings, ChatInputRef } from "../../interfaces";

import styles from "./styles.module.css";
import EscalationAlert from "../common/escalation";
import { useHandleNewMessage, useSelectedContactData } from "../../hooks";

// APIs
import AiApi from "../../api/ai";
import PlaygroundApi from "../../api/playground";
import ContactsApi from "../../api/contacts";

// Stores
import { useSocketStore } from "../../store/socket";
import useContactsStore from "../../store/contacts";

import OnboardingPanel from "./onboarding/onboarding-panel";
import { getPlaygroundStarterPrompts } from "./playground/starter-prompts";

function AiSettings({ settings }: { settings: Settings }) {
  const [content, setContent] = React.useState<string>();
  const [replyTo, setReplyTo] = React.useState<Message | null>(null);
  const [contactId, setContactId] = React.useState<string>("");
  const [typing, setTyping] = React.useState<boolean>(false);
  const chatInputRef = React.useRef<ChatInputRef>(null);
  const hiddenFileInput = React.useRef<HTMLInputElement | null>(null);
  const hiddenDocInput = React.useRef<HTMLInputElement | null>(null);
  const [isOpenUpload, setIsOpenUpload] = React.useState(false);
  const [isLoadingUpload, setIsLoadingUpload] = React.useState(false);
  const [files, setFiles] = React.useState<File[]>([]);
  const [docFiles, setDocFiles] = React.useState<File[]>([]);
  const [isOpenDocsUpload, setIsDocsOpenUpload] =
    React.useState<boolean>(false);
  const { socketReady } = useSocketStore();
  const intl = useIntl();
  const [present] = useIonToast();
  const updateContactsDb = useContactsStore((state) => state.updateContactsDb);
  const playgroundId = settings.playground.id;
  const isOnboarded = settings.onboarded;

  // Contact detail event handler
  const handleContactDetailInfo = (contact: any) => {
    //console.log("setting up contact detail");
    updateContactsDb([contact]);
  };

  const { data: contactInfo, isLoading: isRequestingContact } = useQuery({
    queryFn: () => ContactsApi.getContact(playgroundId),
    queryKey: ["getContact", playgroundId],
    refetchOnWindowFocus: false,
    enabled: playgroundId ? true : false,
  });
  const { data: aiStatusData } = useQuery({
    queryFn: () => AiApi.getAiStatus(),
    queryKey: ["getAiStatus"],
    refetchOnWindowFocus: false,
    staleTime: 300000,
    enabled: isOnboarded,
  });
  const { data: websiteData } = useQuery({
    queryFn: () => AiApi.getWebsite(),
    queryKey: ["getWebsite"],
    refetchOnWindowFocus: false,
    staleTime: 300000,
    enabled: isOnboarded,
  });

  const { contact, escalations, messages } = useSelectedContactData(contactId);
  const contactChannel = contact?.type || "playground";
  const isLoadingContact = isRequestingContact;
  const companyDisplayName = settings.company?.name?.trim() || "";
  const hasUserMessages = messages.some((message) => !message.right);
  const hasConnectedChannels = Boolean(
    aiStatusData?.status?.wa || aiStatusData?.status?.ig || aiStatusData?.status?.fm,
  );
  const crawlContextText = React.useMemo(() => {
    const onboardingSummary = settings.onboarding?.aboutCompany?.trim();
    if (onboardingSummary) {
      return onboardingSummary;
    }

    const fullCrawl = websiteData?.embeddings?.fullCrawl || [];
    const singleCrawl = websiteData?.embeddings?.singleCrawl || [];
    const allEmbeddings = [...fullCrawl, ...singleCrawl];
    const firstWithContent = allEmbeddings.find(
      (embedding: any) =>
        typeof embedding?.embededContent === "string" &&
        embedding.embededContent.trim().length > 0,
    );

    return firstWithContent?.embededContent || "";
  }, [settings.onboarding?.aboutCompany, websiteData?.embeddings]);
  const starterPrompts = React.useMemo(
    () =>
      getPlaygroundStarterPrompts({
        intl,
        companyName: settings.company?.name,
        status: aiStatusData?.status,
        contextText: crawlContextText,
      }),
    [intl, settings.company?.name, aiStatusData?.status, crawlContextText],
  );
  const showStarterPrompts =
    isOnboarded &&
    !isLoadingContact &&
    !hasConnectedChannels &&
    !hasUserMessages &&
    starterPrompts.length > 0;

  React.useEffect(() => {
    if (!isRequestingContact) {
      if (contactInfo && contactInfo?._id) {
        setTyping(false);
        handleContactDetailInfo(contactInfo);
        setContactId(contactInfo._id);
        focusChatInput();
      } else if (isOnboarded) {
        setTyping(false);
        setContactId("");
        focusChatInput();
      }
    }
  }, [contactInfo, isRequestingContact, isOnboarded]);

  React.useEffect(() => {
    console.log("playground contact id", contactId);
  }, [contactId]);

  const {
    data: sendResponse,
    mutate: mutateSendMessage,
    isPending: isSendingMessage,
  } = useMutation({
    mutationFn: PlaygroundApi.sendMessage,
    onSuccess: (data) => {
      if (!data.ok) {
        return present({
          message: intl.formatMessage({ id: "common.tryItLater" }),
          duration: 6000,
          color: "light",
          position: "top",
        });
      }
    },
    onError: () => {
      return present({
        message: intl.formatMessage({ id: "common.tryItLater" }),
        duration: 6000,
        color: "light",
        position: "top",
      });
    },
  });

  const { mutate: mutateClearPlayground, isPending: isClearingPlayground } =
    useMutation({
      mutationFn: PlaygroundApi.clearPlayground,
      onError: () => {
        return present({
          message: intl.formatMessage({ id: "common.tryItLater" }),
          duration: 6000,
          color: "light",
          position: "top",
        });
      },
    });

  const onChangeContent = (newContent: string) => setContent(newContent);

  const onSelectStarterPrompt = (
    prompt: { key: string; text: string },
    index: number,
  ) => {
    if (playgroundId) {
      mutateSendMessage({
        playgroundId,
        content: prompt.text,
        replyTo: replyTo?.id,
      });
      setContent("");
      setReplyTo(null);
      requestAnimationFrame(() => {
        chatInputRef.current?.setInputFocus();
      });
    } else {
      setContent(prompt.text);
      requestAnimationFrame(() => {
        chatInputRef.current?.setInputFocusAtEnd();
      });
    }

    if ((window as any).GA_INITIALIZED) {
      ReactGA.event("playground_starter_prompt_clicked", {
        flow: "playground",
        prompt_key: prompt.key,
        prompt_index: index,
      });
    }
  };

  const onBeforeClearPlayground = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    event.preventDefault();
    if (contactId) {
      mutateClearPlayground({ contactId });
    }
    setTyping(false);
  };

  const onSubmit = () => {
    if (content && playgroundId) {
      mutateSendMessage({
        playgroundId,
        content,
        replyTo: replyTo?.id,
      });
    }
    setContent("");
    setReplyTo(null);
  };

  const onReply = (message: Message, content: string) => {
    if (content && playgroundId) {
      mutateSendMessage({
        playgroundId,
        content,
        replyTo: message ? message.id : undefined,
      });
    }
  };

  const solveEscalations = async () => {
    const { err } = await ContactsApi.putSolveEscalations(contactId);
    if (err) {
      console.log(err);
    }
  };

  React.useEffect(() => {
    if (sendResponse && sendResponse?.ok) {
      setContactId(sendResponse.contactId);
    }
  }, [sendResponse]);

  const focusChatInput = () => chatInputRef.current?.setInputFocus();

  useHandleNewMessage({
    callback: (msg) => {
      setTyping(msg.typing);
    },
    cleanup: () => {
      console.log("Cleanup after unmount or when re-run");
    },
    contactId: contactId,
  });

  const sendPhotos = async (content: FormData) => {
    setIsLoadingUpload(true);
    const { err } = await ContactsApi.uploadMedia(contact.id, content);
    setIsLoadingUpload(false);
    if (err) {
      if (err.error) {
        return present({
          message: err.error?.[intl.locale],
          duration: 6000,
          color: "warning",
          position: "bottom",
        });
      }
    }
    setIsOpenUpload(false);
    return onHandleModalClose();
  };

  const onHandleModalClose = () => {
    setIsDocsOpenUpload(() => false);
    setIsOpenUpload(() => false);

    if (hiddenFileInput?.current?.value) {
      hiddenFileInput.current.value = "";
    }
    if (hiddenDocInput.current?.value) {
      hiddenDocInput.current.value = "";
    }

    setFiles([]);
    return setDocFiles([]);
  };

  const onChangeFileInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "photo" | "doc",
  ) => {
    setIsLoadingUpload(false);
    const fileList = e.target.files;
    if (fileList && fileList.length > 0) {
      const files = [...fileList];

      if (type === "photo") {
        setFiles(files);
        return setIsOpenUpload(true);
      }

      setDocFiles(files);
      return setIsDocsOpenUpload(true);
    }
  };

  const openSendPhoto = () => {
    hiddenFileInput.current?.click();
  };

  const openSendDoc = () => {
    hiddenDocInput.current?.click();
  };

  return (
    <div
      className={classNames("w-100 h-100", {
        ["flex flex-align-center"]: !isOnboarded,
      })}
    >
      <div
        className={classNames(styles.playgroundWrapper, {
          [styles.isOnboarding]: !isOnboarded,
        })}
      >
        {!playgroundId || isLoadingContact ? (
          <div className={styles.chatWrapper}>
            <div className={styles.chatSkeleton}>
              <SkeletonText rows={15} />
            </div>
          </div>
        ) : (
          <div className={styles.chatWrapper}>
            <div className={styles.chatContainer}>
              <div className={styles.chatHeaderContainer}>
                <div className={styles.chatHeaderContent}>
                  <div className="flex gap-3 align-items-center">
                    <h3 className="fs-4 mb-0">
                      {!socketReady ? (
                        <div className="connection-status fs-1 color-step-700 flex align-items-center ">
                          <IonSpinner
                            color="medium"
                            name="lines-sharp-small"
                          ></IonSpinner>{" "}
                          <FormattedMessage id="common.connecting" />
                        </div>
                      ) : (
                        companyDisplayName || <FormattedMessage id="aiAgent" />
                      )}
                    </h3>
                  </div>
                  {isOnboarded ? (
                    <div className="flex gap-3 align-items-center">
                      <button
                        className="btn btn-visito-primary btn-xs btn-flex"
                        onClick={onBeforeClearPlayground}
                        disabled={isClearingPlayground}
                      >
                        <IconRefresh size={15} />
                        <FormattedMessage id="aiSettingsReset" />
                      </button>

                      {settings.isVoiceReady && contactId ? (
                        <Call
                          className={"btn btn-visito-primary btn-xs btn-flex"}
                          icon={
                            <>
                              <IconPhoneCall size={15} />
                              <FormattedMessage id="aiSettingsCall" />
                            </>
                          }
                          onCall={() => console.log("calling")}
                          onHangUp={() => console.log("hanging up")}
                          playgroundId={playgroundId}
                          displayName={settings.company.name}
                          type={"playground"}
                        />
                      ) : null}
                    </div>
                  ) : null}
                </div>

                {contact?.escalated && (
                  <div className="flex flex-column">
                    <EscalationAlert
                      escalation={escalations?.[0]}
                      solveEscalations={() => solveEscalations()}
                      type="playground"
                    />
                  </div>
                )}
              </div>
              <div className="h-100 w-100">
                {isOnboarded ? (
                  <ChatDetail
                    mode={"desktop"}
                    scrollEscalations={1}
                    type="playground"
                    replyTo={({ message }: { message: Message }) => {
                      setReplyTo(message);
                      focusChatInput();
                    }}
                    actionClick={({
                      message,
                      text,
                    }: {
                      message: Message;
                      text: string;
                    }) => onReply(message, text)}
                    actions={["reply", "flag"]}
                    typingIndicator={typing}
                    selectedId={contactId}
                  />
                ) : (
                  <div className="w-100 h-100 flex flex-column align-items-center justify-content-center">
                    <OnboardingPanel />
                  </div>
                )}
              </div>
              <div
                className={classNames(styles.chatInputContainer, {
                  [styles.isOnboardingOpacity]: !isOnboarded,
                })}
              >
                {showStarterPrompts ? (
                  <div className={styles.starterPrompts}>
                    <span className={styles.starterPromptsLabel}>
                      <FormattedMessage id="ai.playground.starters.label" />
                    </span>
                    <div className={styles.starterPromptsRow}>
                      {starterPrompts.map((prompt, index) => (
                        <button
                          key={`${prompt.key}-${index}`}
                          type="button"
                          className={styles.starterPromptPill}
                          disabled={isSendingMessage}
                          onClick={() => onSelectStarterPrompt(prompt, index)}
                        >
                          {prompt.text}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}
                <FileInput
                  ref={hiddenFileInput}
                  accept="image/png, image/jpeg, image/webp"
                  multiple
                  onChange={(e) => onChangeFileInput(e, "photo")}
                  style={{ display: "none" }}
                />
                <FileInput
                  ref={hiddenDocInput}
                  accept="application/pdf, .pdf"
                  onChange={(e) => onChangeFileInput(e, "doc")}
                  style={{ display: "none" }}
                />
                <ChatInput
                  ref={chatInputRef}
                  id="send-body-playground"
                  value={content}
                  placeholder={
                    isOnboarded
                      ? intl.formatMessage({
                          id: "chats.newMessageInput",
                        })
                      : ""
                  }
                  channel={contactChannel}
                  onInputChange={onChangeContent}
                  onOpenPhotoInput={openSendPhoto}
                  onOpenDocInput={openSendDoc}
                  onBeforeSubmit={onSubmit}
                  disabled={isOnboarded ? false : true}
                  replyTo={replyTo}
                  cleanReplyTo={() => setReplyTo(null)}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      <UploadPhotoModal
        show={isOpenUpload || isOpenDocsUpload}
        sending={isLoadingUpload}
        handleClose={onHandleModalClose}
        files={isOpenDocsUpload ? docFiles : files}
        upload={sendPhotos}
        mode={`desktop`}
        areDocs={isOpenDocsUpload}
        channel={contactChannel}
      />
    </div>
  );
}

export default AiSettings;
